// The bounds-checked reader and the writer, ported from internal/wire/wire.go.
//
// The Go reader latches its first error and returns zeroes afterwards, so a
// decoder can read a whole message and check once at the end. This one throws at
// the point of underflow instead, which is the same observable behaviour — Go
// returns the FIRST error, and the first error is the one thrown here — with one
// fewer state machine to keep in step.
//
// All multi-byte fields are little-endian, matching the DataView calls this file
// makes and the binary.LittleEndian calls the Go encoder makes.

import type { Class } from './classes';
import { errClass, errTickRange, errTrailing, errTruncated } from './errors';
import type { TickNo } from './core';

/**
 * A byte view that is definitely backed by a plain ArrayBuffer.
 *
 * TypeScript 5.7 made the typed arrays generic over their buffer, and the DOM
 * signatures that matter here (BufferSource, WebSocket.send) exclude
 * SharedArrayBuffer. Naming the narrow type once is better than casting at five
 * call sites.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

/** Reads a message left to right, refusing to read past its end. */
export class Reader {
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  private off = 0;

  constructor(b: Uint8Array | ArrayBuffer) {
    this.bytes = b instanceof Uint8Array ? b : new Uint8Array(b);
    this.view = new DataView(this.bytes.buffer, this.bytes.byteOffset, this.bytes.byteLength);
  }

  get remaining(): number {
    return this.bytes.byteLength - this.off;
  }

  private need(n: number): number {
    if (n < 0 || this.remaining < n) throw errTruncated();
    const at = this.off;
    this.off += n;
    return at;
  }

  u8(): number {
    return this.view.getUint8(this.need(1));
  }

  u16(): number {
    return this.view.getUint16(this.need(2), true);
  }

  u32(): number {
    return this.view.getUint32(this.need(4), true);
  }

  /**
   * u64 as a plain number. A value past Number.MAX_SAFE_INTEGER is refused
   * rather than rounded: a tick decoded to the wrong number would make every
   * continuity check downstream quietly wrong.
   */
  u64(): number {
    const at = this.need(8);
    const v = this.view.getBigUint64(at, true);
    if (v > BigInt(Number.MAX_SAFE_INTEGER)) throw errTickRange();
    return Number(v);
  }

  /** A borrowed view of n bytes. It aliases the message buffer; copy to keep it. */
  take(n: number): Uint8Array {
    const at = this.need(n);
    return this.bytes.subarray(at, at + n);
  }

  /**
   * A fresh copy of n bytes, for anything that outlives the message buffer.
   *
   * Typed as Bytes rather than Uint8Array because the browser APIs that consume
   * it — DecompressionStream's writer, WebSocket.send — will not accept a view
   * that might be backed by a SharedArrayBuffer, and a message read off a socket
   * never is.
   */
  copy(n: number): Bytes {
    const out = new Uint8Array(n);
    out.set(this.take(n));
    return out;
  }

  /** UTF-8 text of n bytes. */
  text(n: number): string {
    return utf8Decoder.decode(this.take(n));
  }

  /**
   * Consumes the nine-byte header and checks the class is the one the caller's
   * decoder handles.
   */
  header(want: Class): TickNo {
    const c = this.u8() as Class;
    const t = this.u64();
    if (c !== want) throw errClass();
    return t;
  }

  /** Treats leftover bytes as an error. A silently ignored tail is how two implementations drift. */
  done(): void {
    if (this.remaining !== 0) throw errTrailing();
  }
}

const utf8Decoder = new TextDecoder('utf-8', { fatal: false });
const utf8Encoder = new TextEncoder();

/** Appends little-endian fields to a growable buffer. */
export class Writer {
  private buf: Uint8Array;
  private view: DataView;
  private len = 0;

  constructor(capacity = 64) {
    this.buf = new Uint8Array(capacity);
    this.view = new DataView(this.buf.buffer);
  }

  private grow(n: number): number {
    if (this.len + n > this.buf.byteLength) {
      let cap = this.buf.byteLength * 2 || 64;
      while (cap < this.len + n) cap *= 2;
      const next = new Uint8Array(cap);
      next.set(this.buf.subarray(0, this.len));
      this.buf = next;
      this.view = new DataView(this.buf.buffer);
    }
    const at = this.len;
    this.len += n;
    return at;
  }

  // Every one of these takes the offset from grow() on its own line, and that
  // is load-bearing rather than a style choice. Written as
  // `this.view.setUint32(this.grow(4), v, true)` the receiver `this.view` is
  // evaluated BEFORE grow() runs, so a call that reallocates writes through the
  // stale DataView over the old buffer — throwing RangeError once the new
  // offset is past the old length, and silently corrupting before that.
  // Reproduced: a Writer that starts at 8 bytes throws on the third u32.
  // It stayed latent only because every other encoder pre-sizes its Writer
  // exactly, so growth never happened in production.

  u8(v: number): this {
    const at = this.grow(1);
    this.view.setUint8(at, v);
    return this;
  }

  u16(v: number): this {
    const at = this.grow(2);
    this.view.setUint16(at, v, true);
    return this;
  }

  u32(v: number): this {
    const at = this.grow(4);
    this.view.setUint32(at, v, true);
    return this;
  }

  u64(v: number): this {
    const at = this.grow(8);
    this.view.setBigUint64(at, BigInt(v), true);
    return this;
  }

  bytes(b: Uint8Array): this {
    const at = this.grow(b.byteLength);
    this.buf.set(b, at);
    return this;
  }

  /** The nine-byte header every message opens with. */
  header(c: Class, tick: TickNo): this {
    return this.u8(c).u64(tick);
  }

  finish(): Uint8Array {
    return this.buf.slice(0, this.len);
  }
}

/** UTF-8 bytes of a string, for the two encoders that carry text. */
export function encodeUtf8(s: string): Uint8Array {
  return utf8Encoder.encode(s);
}

